import { Component, DestroyRef, EventEmitter, forwardRef, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { ItemProps } from '@app/interfaces';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { RootService } from '@app/core/root.service';
import { debounceTime, map } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { tap } from 'rxjs/internal/operators/tap';
import { distinctUntilChanged } from 'rxjs/internal/operators/distinctUntilChanged';
import { of } from 'rxjs/internal/observable/of';
import { SHARED_IMPORTS } from '@app/shared/shared.imports';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-ng-select-api',
  templateUrl: './ng-select-api.component.html',
  styleUrls: ['./ng-select-api.component.scss'],
  standalone: true,
  imports: [...SHARED_IMPORTS],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => NgSelectApiComponent),
      multi: true
    }
  ]
})
export class NgSelectApiComponent implements OnInit, ControlValueAccessor {
  get value() {
    return this.val;
  }

  set value(val: any) {
    this.val = val;
    this.onChange(val);
    this.onTouched();
  }

  response: any = {};
  items: any = [];
  itemsBuffer: any = [];
  bufferSize = 50;
  numberOfItemsFromEndBeforeFetchingMore = 10;
  loading = false;
  current_page = 1;
  last_page = 1;
  total_records = 0;
  search$ = new Subject<string>();
  params = {
    page: 1
  };
  listsAdditions: any[] = [];

  @Input() item: any;
  @Input() service: RootService;
  @Input() form: any;
  @Input() isEdit: boolean;
  @Input() isClone: boolean;
  @Input() requiredAsterisk: any;
  @Input() field: ItemProps;
  @Input() disabled: boolean;

  @Input() name: string;
  // tslint:disable-next-line
  @Input('value') val: string;
  @Output() valueChanged: EventEmitter<any> = new EventEmitter();
  @Output() StatusChanged: EventEmitter<any> = new EventEmitter();

  constructor(private destroyRef: DestroyRef) {}

  ngOnInit() {
    this.listsSubscription();
    this.listsAdditionsSubscription();
    this.SearchSubscription();
  }

  listsSubscription() {
    this.service.lists$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((listPrefix: string) => {
      if (listPrefix === this.field.form.listPrefix) {
        // console.log('initial');
        // console.log(this.service.lists[this.field.form.listPrefix]);
        this.response = this.service.lists[listPrefix];
        if (!!this.response && !!this.response.data && !!this.response.meta.pagination) {
          this.items = this.response.data && this.response.data.length ? [...this.response.data] : [];
          this.current_page = this.response.meta.pagination.current_page;
          this.last_page = this.response.meta.pagination.last_page;
          this.total_records = this.response.meta.pagination.total_records;
        }
      }
    });
  }

  listsAdditionsSubscription() {
    this.service.listsAdditions$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((event: { listPrefix: string; items: any[] }) => {
        if (event.listPrefix === this.field.form.listPrefix) {
          console.log('listsAdditions');
          console.log(event.listPrefix);
          console.log('event.ids');
          console.log(event.items);
          this.listsAdditions = [...this.listsAdditions, ...event.items];

          this.response = this.service.lists[event.listPrefix];
          let responseData: any[] = [];
          of(this.response.data)
            .pipe(
              map(items =>
                items.filter((item: any) => {
                  const isValid = this.listsAdditions.some((listAddition: any) => {
                    if (item.id !== listAddition.id) {
                      return true;
                    }
                  });
                  if (isValid) {
                    return item;
                  }
                })
              )
            )
            .subscribe(items => {
              responseData = items;
              this.response.data = [...responseData];
              this.listsAdditions.forEach((listAddition: any) => {
                this.response.data.unshift(listAddition);
              });
            });

          if (!!this.response && !!this.response.data && !!this.response.meta.pagination) {
            this.items = this.response.data && this.response.data.length ? [...this.response.data] : [];
            this.current_page = this.response.meta.pagination.current_page;
            this.last_page = this.response.meta.pagination.last_page;
            this.total_records = this.response.meta.pagination.total_records;
          }
        }
      });
  }

  removeDuplicates(item: any, compareItem: any) {
    return item.id !== compareItem.id;
  }

  onChange: any = () => {};
  onTouched: any = () => {};

  registerOnChange(fn: any) {
    this.onChange = fn;
  }

  registerOnTouched(fn: any) {
    this.onTouched = fn;
  }

  writeValue(value: any) {
    if (value) {
      this.value = value;
    }
  }

  onValueChange(field: ItemProps, event: any) {
    this.valueChanged.emit({ field, event });
  }

  SearchSubscription() {
    this.search$
      .pipe(
        debounceTime(200),
        distinctUntilChanged(),
        tap(() => {
          this.loading = true;
        })
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((term: any) => {
        this.searchRequest(term)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe((response: any) => {
            if (!!response && !!response.data && !!response.meta.pagination) {
              this.loading = false;
              this.items = response.data && response.data.length ? [...response.data] : [];
              this.current_page = response.meta.pagination.current_page;
              this.last_page = response.meta.pagination.last_page;
              this.total_records = response.meta.pagination.total_records;
            }
          });
      });
  }

  searchRequest(term: string, key: string = 'name') {
    if (term) {
      this.params[key] = term;
    } else {
      delete this.params[key];
    }
    this.params.page = 1;
    return this.service.resourceGet(this.field.form.dataUrl, true, this.params);
  }

  onScrollToEnd() {
    this.fetchMore();
  }

  // onScroll({ end }: any) {
  //   console.log('end');
  //   console.log(end);
  //   if (this.loading || (this.last_page === this.current_page)) {
  //     return;
  //   }
  //
  //   if (end && (this.last_page > this.current_page)) {
  //     this.fetchMore();
  //   }
  // }

  private fetchMore() {
    if (this.last_page > this.current_page) {
      this.loading = true;
      const nextPage = this.current_page + 1;
      this.service
        .resourceGet(this.field.form.dataUrl, true, { ...this.params, ...{ page: nextPage } })
        .subscribe((response: any) => {
          if (
            response &&
            response.meta.pagination &&
            response.meta.pagination.current_page &&
            response.meta.pagination.last_page &&
            response.meta.pagination.total_records
          ) {
            let responseData: any[] = [];
            of(response.data)
              .pipe(
                map(items =>
                  items.filter((item: any) => {
                    const isValid =
                      this.listsAdditions && this.listsAdditions.length
                        ? this.listsAdditions.some((listAddition: any) => {
                            return item.id !== listAddition.id;
                          })
                        : true;
                    if (isValid) {
                      return item;
                    }
                  })
                )
              )
              .subscribe(items => {
                responseData = items;
              });

            this.items = response.data && response.data.length ? [...this.items, ...responseData] : [];

            this.current_page = response.meta.pagination.current_page;
            this.last_page = response.meta.pagination.last_page;
            this.total_records = response.meta.pagination.total_records;
            this.loading = false;

            console.log(this.items);
          }
        });
    } else {
      return;
    }
  }
}
